import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import { TimeOffRequest } from '../../types';
import { formatDate } from '../../lib/formatDate';
import { REQUEST_TYPE_LABELS, TYPE_COLORS, STATUS_COLORS } from '../../constants';
import { calculateWorkingDays } from '../../utils/dateUtils';

interface RequestHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  selectedEmployee: {
    employee: {
      firstName: string;
      lastName: string;
    };
  } | null;
  requestHistory: TimeOffRequest[];
  loadingHistory: boolean;
  holidays?: any[];
}

const RequestHistoryDialog: React.FC<RequestHistoryDialogProps> = ({
  open,
  onClose,
  selectedEmployee,
  requestHistory,
  loadingHistory,
  holidays = [],
}) => {
  const calculateDays = (startDate: string, endDate: string) => {
    return calculateWorkingDays(startDate, endDate, holidays);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          border: 'none',
          maxHeight: '90vh',
          height: 'auto',
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        fontWeight: 600,
        color: 'text.primary'
      }}>
        Request History - {selectedEmployee ? `${selectedEmployee.employee.firstName} ${selectedEmployee.employee.lastName}` : ''}
      </DialogTitle>
      <DialogContent sx={{ 
        backgroundColor: 'background.paper',
        padding: 0,
        flex: 1,
        overflow: 'auto'
      }}>
        {loadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: 'primary.main' }} />
          </Box>
        ) : requestHistory.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 3 }}>
            <Typography color="text.secondary" variant="h6" sx={{ fontWeight: 500 }}>
              No requests found for the current year.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ padding: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <TableContainer sx={{ 
              flex: 1,
              overflow: 'auto',
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'background.paper'
            }}>
              <Table size="small" sx={{ backgroundColor: 'background.paper' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>End Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Days</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Reason</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Created</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
                {requestHistory
                  .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                  .map((request) => (
                  <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
                        <Chip
                          label={REQUEST_TYPE_LABELS[request.type as keyof typeof REQUEST_TYPE_LABELS] || request.type}
                          color={TYPE_COLORS[request.type as keyof typeof TYPE_COLORS] as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>{formatDate(request.startDate)}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>{formatDate(request.endDate)}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary', fontWeight: 500 }}>
                        {calculateDays(request.startDate, request.endDate)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary', maxWidth: 200 }}>
                        <Typography variant="body2" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          color: 'text.primary'
                        }}>
                          {request.reason || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
                        <Chip
                          label={request.status}
                          color={STATUS_COLORS[request.status as keyof typeof STATUS_COLORS] as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>{formatDate(request.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        padding: 2
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderColor: 'divider',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestHistoryDialog;
